/*
  Fuego & Fragancia — galería de trabajos hechos.

  Hace dos cosas y nada más: filtrar por categoría y ampliar una foto.

  Por qué se esconde con `data-oculto` y no con `hidden`
  -----------------------------------------------------
  `[hidden]` en el navegador vale `display: none` con la fuerza de un
  selector de atributo, y cualquier regla de clase que declare `display` le
  gana. Las fichas de la rejilla la declaran. Con `hidden` el filtro parecía
  no hacer nada: se marcaba el elemento y seguía viéndose. El atributo propio
  lo acompaña una regla de clase en el CSS, que sí pesa lo suficiente.

  Por qué la ampliación cuelga de <body>
  --------------------------------------
  Si vive dentro de la sección, cualquier `overflow: hidden` o `transform` de
  un antepasado recorta una capa `position: fixed`. Colgarla del final del
  documento la deja fuera del alcance de todo eso.
*/
(function () {
  if (!window.customElements || customElements.get('ff-gallery')) return;

  customElements.define(
    'ff-gallery',
    class FfGallery extends HTMLElement {
      connectedCallback() {
        this.fichas = Array.from(this.querySelectorAll('[data-ff-gal-item]'));
        this.aviso = this.querySelector('[data-ff-gal-empty]');
        if (!this.fichas.length) return;

        this.montarFiltros();
        this.montarAmpliacion();
      }

      disconnectedCallback() {
        this.cerrar();
      }

      /* ---------------- Filtros ---------------- */

      montarFiltros() {
        const botones = Array.from(this.querySelectorAll('[data-ff-gal-chip]'));
        if (!botones.length) return;

        botones.forEach((boton) => {
          boton.addEventListener('click', () => {
            const cat = boton.dataset.ffGalChip;

            botones.forEach((otro) => {
              const activo = otro === boton;
              otro.classList.toggle('is-active', activo);
              otro.setAttribute('aria-pressed', activo ? 'true' : 'false');
            });

            let visibles = 0;
            this.fichas.forEach((ficha) => {
              const entra = cat === '*' || ficha.dataset.cat === cat;
              if (entra) {
                ficha.removeAttribute('data-oculto');
                visibles += 1;
              } else {
                ficha.setAttribute('data-oculto', 'true');
              }
            });

            if (this.aviso) {
              if (visibles === 0) this.aviso.removeAttribute('data-oculto');
              else this.aviso.setAttribute('data-oculto', 'true');
            }
          });
        });
      }

      /* ---------------- Ampliación ---------------- */

      montarAmpliacion() {
        if (this.dataset.lightbox !== 'true') return;

        // Solo las fichas que traen foto: una ficha sin imagen abriría un
        // hueco negro.
        this.ampliables = this.fichas.filter((f) => f.dataset.full);
        if (!this.ampliables.length) return;

        this.ampliables.forEach((ficha) => {
          const media = ficha.querySelector('.ff-gallery__media');
          if (!media) return;
          media.addEventListener('click', () => {
            this.abrir(this.ampliables.indexOf(ficha));
          });
        });
      }

      abrir(indice) {
        this.indice = indice;
        if (!this.capa) this.crearCapa();

        this.pintar();
        document.body.appendChild(this.capa);
        // Un fotograma de margen: sin él el navegador pinta el estado final
        // de golpe y no se ve la entrada.
        requestAnimationFrame(() => this.capa.classList.add('is-open'));

        this.alTeclado = (evento) => {
          if (evento.key === 'Escape') this.cerrar();
          if (evento.key === 'ArrowRight') this.mover(1);
          if (evento.key === 'ArrowLeft') this.mover(-1);
        };
        document.addEventListener('keydown', this.alTeclado);

        // Se congela el scroll del fondo mientras la capa está abierta.
        this.scrollPrevio = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }

      crearCapa() {
        const capa = document.createElement('div');
        capa.className = 'ff-gallery-lb';
        capa.setAttribute('role', 'dialog');
        capa.setAttribute('aria-modal', 'true');

        const etiquetaCerrar = this.dataset.closeLabel || 'Close';

        const figura = document.createElement('figure');
        figura.className = 'ff-gallery-lb__figure';

        const img = document.createElement('img');
        img.className = 'ff-gallery-lb__img';
        figura.appendChild(img);

        const cuerpo = document.createElement('figcaption');
        cuerpo.className = 'ff-gallery-lb__body';
        const titulo = document.createElement('span');
        titulo.className = 'ff-gallery-lb__title';
        const pie = document.createElement('span');
        pie.className = 'ff-gallery-lb__caption';
        cuerpo.appendChild(titulo);
        cuerpo.appendChild(pie);
        figura.appendChild(cuerpo);

        capa.appendChild(figura);

        const cerrar = this.boton('ff-gallery-lb__close', 'x', etiquetaCerrar);
        cerrar.addEventListener('click', () => this.cerrar());
        capa.appendChild(cerrar);

        // Las flechas solo tienen sentido con más de una foto.
        if (this.ampliables.length > 1) {
          const anterior = this.boton('ff-gallery-lb__prev', '<', 'Anterior');
          anterior.addEventListener('click', () => this.mover(-1));
          capa.appendChild(anterior);

          const siguiente = this.boton('ff-gallery-lb__next', '>', 'Siguiente');
          siguiente.addEventListener('click', () => this.mover(1));
          capa.appendChild(siguiente);
        }

        // Pulsar el fondo cierra; pulsar la foto, no.
        capa.addEventListener('click', (evento) => {
          if (evento.target === capa) this.cerrar();
        });

        this.capa = capa;
        this.capaImg = img;
        this.capaTitulo = titulo;
        this.capaPie = pie;
      }

      boton(clase, signo, etiqueta) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ff-gallery-lb__btn ' + clase;
        b.setAttribute('aria-label', etiqueta);
        b.textContent = signo;
        return b;
      }

      pintar() {
        const ficha = this.ampliables[this.indice];
        if (!ficha) return;

        const titulo = ficha.dataset.title || '';
        const pie = ficha.dataset.caption || '';

        this.capaImg.src = ficha.dataset.full;
        this.capaImg.alt = titulo;

        this.capaTitulo.textContent = titulo;
        this.capaPie.textContent = pie;
        // Se vacían de verdad para que no quede el hueco del renglón cuando
        // una foto no trae título ni pie.
        this.capaTitulo.style.display = titulo ? '' : 'none';
        this.capaPie.style.display = pie ? '' : 'none';
      }

      mover(paso) {
        const total = this.ampliables.length;
        // El resto doble evita el índice negativo al retroceder desde la
        // primera foto: da la vuelta al final, como se espera.
        this.indice = ((this.indice + paso) % total + total) % total;
        this.pintar();
      }

      cerrar() {
        if (!this.capa || !this.capa.parentNode) return;

        this.capa.classList.remove('is-open');
        document.removeEventListener('keydown', this.alTeclado);
        document.body.style.overflow = this.scrollPrevio || '';

        const capa = this.capa;
        setTimeout(() => {
          if (capa.parentNode) capa.parentNode.removeChild(capa);
        }, 280);
      }
    }
  );
})();
